/** Register your structure: sites, meters and expected sources from one file - **ADR-0091**.
 *
 * A customer's readings can be loaded from a workbook (Bulk edit); the structure those readings
 * hang from - the sites, the supply points at them, and the streams the census should expect - used
 * to be keyed one at a time. This is the file for it, and it is deliberately the opposite of Bulk
 * edit in the way that matters: **it only ever creates, and it is all or nothing.**
 *
 * Four things the screen has to say that a list of counts does not:
 *
 * - **A refused row refuses the file.** An expected source that failed to register is the absence of
 *   the thing that makes absence visible, so the product never leaves a half-registered estate. The
 *   check lists *every* problem at once, so it is one correction pass and not one per row.
 * - **A row that already exists as stated is "unchanged", not an error.** Sending the file again
 *   after a timeout writes nothing and says so; the screen tells a reader who lost a response that
 *   the remedy is to send the same file again.
 * - **A row that exists with different details is refused, and the sentence says where to change
 *   it.** This file never amends: the Organisation screen does, and does it with dates.
 * - **A blank lease treatment holds the filing the site contributes to.** That is stated before the
 *   file is chosen, not discovered after fifty sites hold a filing.
 *
 * **Who may do it (design rule 6).** An Admin: the file creates sites and meters, which are Admin
 * acts one at a time. Anyone else is told who does it and shown no control - a file picker that
 * 403s is not a permission check.
 *
 * **The check and the apply take the same `File` object.** A second pick would let the file change
 * between looking and applying.
 */

import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";

import { useMe } from "../api/auth";
import {
  StructureRefused,
  structureTemplateUrl,
  useStructureApply,
  useStructureDryRun,
} from "../api/structure";
import type { StructureApplied, StructureCounts, StructureDryRun, StructureRow } from "../api/structure";
import { WriteFailed } from "../components/Loaded";
import { Panel } from "../components/Panel";
import { RequireSession } from "../shell/RequireSession";
import { ScreenFrame } from "./ScreenFrame";
import styles from "./Screen.module.css";
import own from "./Structure.module.css";
import { keepSearch } from "./search";
import { useScreen } from "./useScreen";

/** The tables, in the order the file applies them, named as the customer sees them. */
const TABLES: ReadonlyArray<{ key: "sites" | "meters" | "expected_sources"; label: string }> = [
  { key: "sites", label: "Sites" },
  { key: "meters", label: "Meters" },
  { key: "expected_sources", label: "Expected sources" },
];

const SECTION_LABEL: Readonly<Record<string, string>> = {
  sites: "Site",
  meters: "Meter",
  expected_sources: "Expected source",
};

const DISPOSITION_LABEL: Readonly<Record<string, string>> = {
  create: "Would register",
  unchanged: "Already there",
  refused: "Cannot register",
};

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The three counts as a strip. `refused` is shown even at zero: a reader who is told *"0 refused"*
 * has been told the file was read, which is not what a missing number says. */
function Counts({ result }: { result: StructureDryRun | StructureApplied }) {
  return (
    <div className={own.counts} data-testid="structure-counts">
      {TABLES.map(({ key, label }) => {
        const counts: StructureCounts = result[key];
        return (
          <div key={key} className={own.count} data-testid={`structure-count-${key}`}>
            <span className={own.countLabel}>{label}</span>
            <span>{counts.create} new</span>
            <span>{counts.unchanged} already there</span>
            <span className={counts.refused > 0 ? own.refusedCount : undefined}>
              {counts.refused} refused
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Every row's verdict. **Refused rows first**, because they are what the reader has to act on, and
 * the line number is the one their spreadsheet shows. */
function Rows({ rows, done }: { rows: readonly StructureRow[]; done: boolean }) {
  const ordered = [
    ...rows.filter((row) => row.disposition === "refused"),
    ...rows.filter((row) => row.disposition !== "refused"),
  ];
  if (ordered.length === 0) return null;
  return (
    <table className={own.rows} data-testid="structure-rows">
      <thead>
        <tr>
          <th scope="col">Table</th>
          <th scope="col">Line</th>
          <th scope="col">Row</th>
          <th scope="col">Result</th>
          <th scope="col">Why</th>
        </tr>
      </thead>
      <tbody>
        {ordered.map((row) => (
          <tr
            key={`${row.section}-${row.line}`}
            className={row.disposition === "refused" ? own.refusedRow : undefined}
            data-testid={`structure-row-${row.section}-${row.line}`}
            data-disposition={row.disposition}
          >
            <td>{SECTION_LABEL[row.section] ?? row.section}</td>
            <td>{row.line}</td>
            <td>{row.label}</td>
            <td>
              {row.disposition === "create" && done
                ? "Registered"
                : (DISPOSITION_LABEL[row.disposition] ?? row.disposition)}
            </td>
            <td>
              {row.refusals.length === 0 ? null : (
                <ul className={own.reasons}>
                  {row.refusals.map((refusal) => (
                    <li key={`${refusal.code}-${refusal.column ?? ""}`} data-code={refusal.code}>
                      {refusal.message}
                      {refusal.remedy ? <span className={own.remedy}> {refusal.remedy}</span> : null}
                    </li>
                  ))}
                </ul>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StructureBody() {
  const dryRun = useStructureDryRun();
  const apply = useStructureApply();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  /** Whether the person has pressed the first button: the apply is two presses, and the second says
   * exactly what it will register. */
  const [confirming, setConfirming] = useState(false);

  const checked = dryRun.data;
  const refusal =
    (apply.error instanceof StructureRefused ? apply.error : null) ??
    (dryRun.error instanceof StructureRefused ? dryRun.error : null);
  const failure = apply.error ?? dryRun.error;

  function choose(next: File | null) {
    // A different file is a different question: what was checked, confirmed or registered about the
    // last one says nothing about this one.
    setFile(next);
    setConfirming(false);
    dryRun.reset();
    apply.reset();
  }

  function check() {
    if (!file) return;
    setConfirming(false);
    apply.reset();
    dryRun.mutate(file);
  }

  function register() {
    if (!file) return;
    apply.mutate(file, { onSuccess: () => setConfirming(false) });
  }

  const created = checked
    ? `${plural(checked.sites.create, "site", "sites")}, ${plural(checked.meters.create, "meter", "meters")} and ${plural(checked.expected_sources.create, "expected source", "expected sources")}`
    : "";

  return (
    <>
      <Panel
        title="1. Download the template"
        sub="Sites first, then the meters at them, then what you expect to arrive"
        column="1 / 13"
      >
        <p className={styles.note}>
          <a href={structureTemplateUrl("xlsx")} download data-testid="structure-template-xlsx">
            Download the workbook
          </a>{" "}
          or{" "}
          <a href={structureTemplateUrl("csv")} download data-testid="structure-template-csv">
            the CSV
          </a>
          . Three tables: <strong>Sites</strong>, <strong>Meters</strong> and{" "}
          <strong>Expected sources</strong>. The rows that begin <code>EXAMPLE</code> show the
          format and are never registered; replace or delete them.
        </p>
        <ul className={own.rules} data-testid="structure-rules">
          <li>
            <strong>This file only creates.</strong> A site, meter or stream that already exists is
            left exactly as it is, and a row that disagrees with it is refused. Change an existing
            site from the <Link to="/organisation" search={keepSearch}>Organisation</Link> screen.
          </li>
          <li>
            <strong>If any row is refused, nothing is registered.</strong> You see every problem at
            once, fix the file and send it again.
          </li>
          <li>
            <strong>Companies must exist first</strong>, and no one is named in the file: owners are
            assigned to each stream afterwards.
          </li>
          <li>
            <strong>Leave the lease treatment blank</strong> unless your accountant has confirmed
            it. A site with no answer holds any filing it contributes to, and says why; no basis is
            taken from a file.
          </li>
          <li>
            <strong>At most 500 rows</strong> in total across the three tables. A bigger estate goes
            in more than one file, sites first.
          </li>
        </ul>
      </Panel>

      <Panel
        title="2. Check your file"
        sub="Nothing is registered by checking"
        column="1 / 13"
      >
        <p className={own.actions}>
          <input
            ref={input}
            type="file"
            accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            aria-label="Your structure file"
            data-testid="structure-file"
            onChange={(event) => choose(event.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            className={styles.primary}
            disabled={!file || dryRun.isPending}
            onClick={check}
            data-testid="structure-check"
          >
            {dryRun.isPending ? "Checking…" : "Check this file"}
          </button>
        </p>

        {failure && !refusal?.isFileRefused ? (
          <WriteFailed
            error={failure}
            of="this structure file"
            retrySafe
            testId="structure-error"
            onRetry={apply.error ? register : check}
          />
        ) : null}

        {checked ? (
          <div data-testid="structure-checked">
            <p className={checked.can_apply ? styles.note : own.warning} role="status">
              {checked.sentence}
            </p>
            <p className={styles.note} data-testid="structure-ceiling">
              This file has {checked.total_rows} {checked.total_rows === 1 ? "row" : "rows"}; one
              file can hold {checked.row_ceiling}.
            </p>
            {checked.ignored_sheets.length ? (
              <p className={styles.note} data-testid="structure-ignored">
                Not read: {checked.ignored_sheets.join(", ")}. Only the Sites, Meters and Expected
                sources tables are.
              </p>
            ) : null}
            {checked.ceiling_problem ? null : (
              <>
                <Counts result={checked} />
                <Rows rows={checked.rows} done={false} />
              </>
            )}
          </div>
        ) : null}
      </Panel>

      {checked?.can_apply && !apply.data ? (
        <Panel
          title="3. Register"
          sub="Nothing above is registered until you confirm here"
          column="1 / 13"
        >
          {confirming ? (
            <div data-testid="structure-confirm">
              <p className={styles.note}>
                This registers {created}. A site is never deleted afterwards, only closed. If this
                request is interrupted, send the same file again: what was registered is left alone.
              </p>
              <p className={own.actions}>
                <button
                  type="button"
                  className={styles.primary}
                  disabled={apply.isPending}
                  onClick={register}
                  data-testid="structure-register"
                >
                  {apply.isPending ? "Registering…" : "Confirm and register"}
                </button>
                <button
                  type="button"
                  className={styles.secondary}
                  disabled={apply.isPending}
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </button>
              </p>
            </div>
          ) : (
            <p className={own.actions}>
              <button
                type="button"
                className={styles.primary}
                onClick={() => setConfirming(true)}
                data-testid="structure-begin"
              >
                Register {created}
              </button>
            </p>
          )}
        </Panel>
      ) : null}

      {refusal?.isFileRefused ? (
        <Panel title="This file was not registered" column="1 / 13">
          <p className={own.warning} role="alert" data-testid="structure-refused">
            {refusal.message}
          </p>
          <Rows rows={refusal.rows} done={false} />
        </Panel>
      ) : null}

      {apply.data ? (
        <Panel title="Done" column="1 / 13">
          <p className={styles.note} role="status" data-testid="structure-applied">
            {apply.data.already_registered
              ? "This file was already registered, so nothing was written. "
              : ""}
            {apply.data.sentence}
          </p>
          <Counts result={apply.data} />
          <p className={styles.note}>
            Next: declare who owns each expected source from{" "}
            <Link to="/sources" search={keepSearch}>
              the expected bills
            </Link>
            , and check the sites on the{" "}
            <Link to="/organisation" search={keepSearch}>
              Organisation
            </Link>{" "}
            screen.
          </p>
        </Panel>
      ) : null}
    </>
  );
}

export function StructureScreen() {
  const screen = useScreen();
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";

  return (
    <RequireSession>
      <ScreenFrame
        screen={screen}
        title="Register your structure"
        crumb="sites, meters and expected sources from a file"
        bodyScrolls
      >
        {isAdmin ? (
          <StructureBody />
        ) : (
          <Panel title="Who registers a structure" column="1 / 13">
            {/* Told who does it, rather than shown a file picker that predictably 403s. A file
                that creates sites and meters is an Admin's act for the reason creating one is. */}
            <p className={styles.note} data-testid="structure-who-can">
              An <strong>Admin</strong> registers a company&rsquo;s sites, meters and expected
              sources from a file. The file creates the sites and supply points that every figure
              and every filing is about, so it is not a shared setting. Ask an Admin, or add a
              single site from the{" "}
              <Link to="/organisation" search={keepSearch}>
                Organisation
              </Link>{" "}
              screen if you are allowed to see it.
            </p>
          </Panel>
        )}
      </ScreenFrame>
    </RequireSession>
  );
}
